import { Box } from "@remo-co/ui-core/src/components/Box";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogActions } from "@remo-co/ui-core/src/components/DialogActions";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useI18n } from "i18n";
import { selectUser } from "modules/auth/redux/selectors";
import useUpdateMyProfile from "modules/userProfile/hooks/useUpdateMyProfile";
import { useEffect, useState } from "react";
import {
  trialFirstNameKey,
  trialLastNameKey,
  trialPhoneKey,
} from "modules/auth/constants";
import { IUser } from "modules/app/types";
import { useSelector } from "react-redux";
import { useStyles } from "./styles";

export const isUsersNameMissing = (user?: IUser) =>
  !!user &&
  (!user.profile.name || user.profile.name.trim() === "") &&
  (!user.profile.firstName || user.profile.firstName.trim() === "") &&
  (!user.profile.lastName || user.profile.lastName.trim() === "");

const MissingFullNameDialog = () => {
  const { t } = useI18n(["auth", "common"]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSettingFromLocal, setIsSettingFromLocal] = useState(true);
  const user = useSelector(selectUser);

  const { mutate: updateMyProfile, isPending } = useUpdateMyProfile();
  const styles = useStyles();

  const isOpen = isUsersNameMissing(user);

  useEffect(() => {
    if (!user) return;

    const asyncFunction = async () => {
      const firstNameLocal = localStorage.getItem(trialFirstNameKey);
      const lastNameLocal = localStorage.getItem(trialLastNameKey);
      const phoneNumberLocal = localStorage.getItem(trialPhoneKey);

      if (firstNameLocal && lastNameLocal && phoneNumberLocal) {
        await updateMyProfile({
          firstName: firstNameLocal,
          lastName: lastNameLocal,
          phone: phoneNumberLocal,
        });

        localStorage.removeItem(trialFirstNameKey);
        localStorage.removeItem(trialLastNameKey);
        localStorage.removeItem(trialPhoneKey);
      }

      setIsSettingFromLocal(false);
    };

    asyncFunction();
  }, [updateMyProfile, user]);

  if (isSettingFromLocal) return null;

  return (
    <Dialog
      open={isOpen}
      classes={{
        root: styles.dialog,
      }}
      data-testid="missing-full-name-dialog"
    >
      <Container
        flex
        justifyContent="space-between"
        alignItems="center"
        className={styles.header}
      >
        <Typography variant="h4">{t("auth:enter.name")}</Typography>
      </Container>
      <form
        data-testid="full-name-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();

          if (!(firstName && lastName) || isPending) {
            return;
          }

          updateMyProfile({
            firstName,
            lastName,
          });
        }}
      >
        <DialogContent className={styles.form}>
          <Box display="flex" className={styles.inputs}>
            <Input
              name="firstName"
              required
              autoFocus
              onChange={(event) => {
                setFirstName(event.target.value);
              }}
              label={t("common:first.name")}
              size="sm"
              value={firstName}
              inputProps={{ "data-testid": "first-name-input" }}
            />
            <Input
              name="lastName"
              required
              onChange={(event) => {
                setLastName(event.target.value);
              }}
              label={t("common:last.name")}
              size="sm"
              value={lastName}
              inputProps={{ "data-testid": "last-name-input" }}
            />
          </Box>
        </DialogContent>
        <DialogActions className={styles.actions}>
          <Button
            color="blue"
            size="md"
            variant="secondary"
            loading={isPending}
            disabled={isPending || !(firstName && lastName)}
            data-testid="save-full-name"
            type="submit"
          >
            {t("common:button.save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MissingFullNameDialog;
